import React, { useEffect, useRef } from 'react';

// A light-mode take on vgpu.sh's "FFT ocean" example: a deep-water surface
// driven by a real inverse FFT (Phillips spectrum + gravity-wave dispersion),
// rendered as a perspective field of particles riding the waves. Where the
// original blooms bright points over black, this inverts the scheme: ink-blue
// points that deepen on the crests, fading into a pale sky gradient.
//
// The FFT runs on the CPU (64x64, well under a millisecond) and is uploaded as
// a texture; everything per-particle happens in a WebGL2 vertex shader, which
// is what lets the field be a few hundred thousand small translucent points,
// lit from the surface normal like the original, rather than a few thousand
// opaque squares.

const N = 64; // FFT grid resolution (power of two)
const LOG_N = 6;
const PATCH = 64; // world-space size of one ocean tile
const GRAVITY = 9.81;
const WIND_SPEED = 6.5;
const WIND_X = Math.SQRT1_2;
const WIND_Z = Math.SQRT1_2;
const CHOPPINESS = 1.5;
const TARGET_AMP = 2.4; // world-space amplitude after auto-gain

const CAM_HEIGHT = 8;
const NEAR_Z = 4; // starts well past the bottom edge so a tall crest can't lift the first row into view
const FAR_Z = 260;
const SPACING = 2.6; // average distance between particles, in CSS px
const MAX_PARTICLES = 450000;
const OVERSCAN = 1.3; // widen each row past the frustum so side edges stay hidden
// Extra world-space width per side on top of OVERSCAN. Choppy waves push
// particles sideways by a fixed world distance, which near the camera (and on
// narrow windows) is more than the proportional overscan covers.
const SIDE_MARGIN = 3;
const HORIZON = 0.22; // horizon line, as a fraction of canvas height
const TIME_SCALE = 0.5;
// Pointer disturbances. Amplitudes are world-space heights (the swell itself
// peaks at TARGET_AMP); lifetimes are in scene seconds (real seconds * TIME_SCALE).
const CLICK_AMP = 0.8;
const CLICK_LIFE = 3.5;
// The cursor wake is a trail of small ripples. Their sizes are fractions of
// the depth they were dropped at, so they look the same on screen at any distance.
const WAKE_AMP = 0.016; // crest height at full cursor speed
const WAKE_RADIUS = 0.2; // how far a ripple spreads over its life
const WAKE_WIDTH = 0.04; // crest thickness
const WAKE_LIFE = 0.7;
const WAKE_STEP = 36; // cursor px travelled between ripples
const WAKE_INTERVAL = 60; // and at least this many ms apart, which keeps a fast sweep under WAKE_MAX
const WAKE_FULL_SPEED = 0.8; // cursor px/ms at which ripples reach full height
const WAKE_MAX = 24;
const WAKE_ENABLED = false; // off for now

// ---------------------------------------------------------------------------
// FFT machinery
// ---------------------------------------------------------------------------

const bitRev = new Uint32Array(N);
for (let i = 0; i < N; i++) {
  let r = 0;
  for (let b = 0; b < LOG_N; b++) r |= ((i >> b) & 1) << (LOG_N - 1 - b);
  bitRev[i] = r;
}

// Twiddle factors per stage, with the +i sign convention (inverse transform).
const twiddles = [];
for (let len = 2; len <= N; len <<= 1) {
  const half = len >> 1;
  const re = new Float32Array(half);
  const im = new Float32Array(half);
  for (let k = 0; k < half; k++) {
    const ang = (2 * Math.PI * k) / len;
    re[k] = Math.cos(ang);
    im[k] = Math.sin(ang);
  }
  twiddles.push({ re, im });
}

function ifft1d(re, im, off, stride) {
  for (let i = 0; i < N; i++) {
    const j = bitRev[i];
    if (j > i) {
      const a = off + i * stride;
      const b = off + j * stride;
      let t = re[a]; re[a] = re[b]; re[b] = t;
      t = im[a]; im[a] = im[b]; im[b] = t;
    }
  }
  for (let s = 0; s < LOG_N; s++) {
    const len = 2 << s;
    const half = len >> 1;
    const tw = twiddles[s];
    for (let base = 0; base < N; base += len) {
      for (let k = 0; k < half; k++) {
        const a = off + (base + k) * stride;
        const b = off + (base + k + half) * stride;
        const wr = tw.re[k];
        const wi = tw.im[k];
        const xr = re[b] * wr - im[b] * wi;
        const xi = re[b] * wi + im[b] * wr;
        re[b] = re[a] - xr;
        im[b] = im[a] - xi;
        re[a] += xr;
        im[a] += xi;
      }
    }
  }
}

function ifft2d(re, im) {
  for (let row = 0; row < N; row++) ifft1d(re, im, row * N, 1);
  for (let col = 0; col < N; col++) ifft1d(re, im, col, N);
}

// ---------------------------------------------------------------------------
// Ocean simulation: Phillips spectrum evolved by the deep-water dispersion
// relation, inverse-transformed into height + choppy displacement fields.
// ---------------------------------------------------------------------------

function createOcean() {
  const size = N * N;
  const h0re = new Float32Array(size);
  const h0im = new Float32Array(size);
  const omega = new Float32Array(size);
  const kxn = new Float32Array(size);
  const kzn = new Float32Array(size);
  const mirror = new Uint32Array(size);

  const gauss = () => {
    let u = 0;
    let v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  const largestWave = (WIND_SPEED * WIND_SPEED) / GRAVITY;
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const idx = j * N + i;
      const kx = (2 * Math.PI * (i <= N / 2 ? i : i - N)) / PATCH;
      const kz = (2 * Math.PI * (j <= N / 2 ? j : j - N)) / PATCH;
      const k = Math.sqrt(kx * kx + kz * kz);
      mirror[idx] = ((N - j) % N) * N + ((N - i) % N);
      if (k < 1e-6) continue;
      const dot = (kx * WIND_X + kz * WIND_Z) / k;
      let phillips =
        (Math.exp(-1 / (k * largestWave * (k * largestWave))) / (k * k * k * k)) *
        dot * dot *
        Math.exp(-k * k * 0.25); // damp the shortest wavelengths
      if (dot < 0) phillips *= 0.25; // suppress waves running against the wind
      const amp = Math.sqrt(phillips / 2);
      h0re[idx] = gauss() * amp;
      h0im[idx] = gauss() * amp;
      omega[idx] = Math.sqrt(GRAVITY * k);
      kxn[idx] = kx / k;
      kzn[idx] = kz / k;
    }
  }

  const hRe = new Float32Array(size);
  const hIm = new Float32Array(size);
  const dxRe = new Float32Array(size);
  const dxIm = new Float32Array(size);
  const dzRe = new Float32Array(size);
  const dzIm = new Float32Array(size);

  // Interleaved (dx, height, dz, unused) per texel, for the GPU.
  const packed = new Float32Array(size * 4);
  const ocean = { packed, peak: 0 };

  ocean.update = (t) => {
    for (let idx = 0; idx < size; idx++) {
      const m = mirror[idx];
      const c = Math.cos(omega[idx] * t);
      const s = Math.sin(omega[idx] * t);
      // h(k,t) = h0(k) e^{iwt} + h0*(-k) e^{-iwt}
      const re = h0re[idx] * c - h0im[idx] * s + h0re[m] * c - h0im[m] * s;
      const im = h0re[idx] * s + h0im[idx] * c - h0re[m] * s - h0im[m] * c;
      hRe[idx] = re;
      hIm[idx] = im;
      // D(k) = -i (k/|k|) h(k)
      dxRe[idx] = kxn[idx] * im;
      dxIm[idx] = -kxn[idx] * re;
      dzRe[idx] = kzn[idx] * im;
      dzIm[idx] = -kzn[idx] * re;
    }
    ifft2d(hRe, hIm);
    ifft2d(dxRe, dxIm);
    ifft2d(dzRe, dzIm);

    let peak = 1e-6;
    for (let idx = 0; idx < size; idx++) {
      const a = Math.abs(hRe[idx]);
      if (a > peak) peak = a;
      packed[idx * 4] = dxRe[idx];
      packed[idx * 4 + 1] = hRe[idx];
      packed[idx * 4 + 2] = dzRe[idx];
    }
    // Snap to the measured peak on the first frame so the surface doesn't
    // visibly rescale while the auto-gain settles.
    ocean.peak = ocean.peak === 0 ? peak : ocean.peak + (peak - ocean.peak) * 0.05;
  };

  return ocean;
}

// ---------------------------------------------------------------------------
// Rendering (WebGL2)
// ---------------------------------------------------------------------------

const MAX_RIPPLES = 6;
const fl = (n) => Number(n).toFixed(5);

// Each particle is a point on a screen-uniform lattice over the water plane:
// aUV.x runs across the (overscanned) view, aUV.y from the far row to the near
// one in equal steps of 1/z, which are equal steps down the screen.
const PARTICLE_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aUV;

uniform sampler2D uDisp;
uniform vec2 uView;   // canvas size, CSS px
uniform float uViewH; // screen height the scene is composed against, CSS px
uniform float uDpr;
uniform float uGain;
uniform int uRippleCount;
uniform vec3 uRipples[${MAX_RIPPLES}]; // x, z, age
uniform int uWakeCount;
uniform vec4 uWake[${WAKE_MAX}]; // x, z, age (0..1 of its life), strength

out vec4 vColor;

const float PATCH = ${fl(PATCH)};
const float TEXEL = ${fl(PATCH / N)};
const float CAM_HEIGHT = ${fl(CAM_HEIGHT)};
const float INV_NEAR = ${fl(1 / NEAR_Z)};
const float INV_FAR = ${fl(1 / FAR_Z)};
// Depth fade is normalized to the depth range actually on screen (the bottom
// edge sits around z ~ 9.6 for this camera), not to NEAR_Z, which lies below
// the frame purely as overscan.
const float INV_VIS_NEAR = ${fl(1 / 8.5)};
const float CHOP = ${fl(CHOPPINESS)};

// Pale steel blue in the troughs deepening to ink blue on the crests (the
// inverse of the original's dark-to-bloom ramp).
const vec3 PALE = vec3(197.0, 219.0, 238.0) / 255.0;
const vec3 MID = vec3(64.0, 128.0, 196.0) / 255.0;
const vec3 INK = vec3(9.0, 58.0, 138.0) / 255.0;
const vec3 LIGHT = normalize(vec3(-0.4, 0.55, 0.75)); // low sun, ahead and to the left

// Bicubic (B-spline) sample of the displacement texture, built from four
// bilinear taps. Plain bilinear filtering is only continuous in value: its
// slope jumps at every texel edge, and since the shading comes from the slope
// that showed up as a rectangular grid across the water.
vec3 field(vec2 p) {
  vec2 st = p / TEXEL;
  vec2 i = floor(st);
  vec2 f = st - i;
  vec2 f2 = f * f;
  vec2 f3 = f2 * f;
  vec2 w0 = (1.0 - 3.0 * f + 3.0 * f2 - f3) / 6.0;
  vec2 w1 = (4.0 - 6.0 * f2 + 3.0 * f3) / 6.0;
  vec2 w3 = f3 / 6.0;
  vec2 g0 = w0 + w1;
  vec2 g1 = 1.0 - g0;
  vec2 a = (i - 0.5 + w1 / g0) / ${fl(N)};
  vec2 b = (i + 1.5 + w3 / g1) / ${fl(N)};
  return g0.y * (g0.x * textureLod(uDisp, a, 0.0).rgb + g1.x * textureLod(uDisp, vec2(b.x, a.y), 0.0).rgb)
       + g1.y * (g0.x * textureLod(uDisp, vec2(a.x, b.y), 0.0).rgb + g1.x * textureLod(uDisp, b, 0.0).rgb);
}

// Click ripples and the cursor wake. Both are one crest travelling outward;
// the wake's are smaller, shorter-lived and dropped along the cursor's path.
float disturbance(vec2 p) {
  float h = 0.0;
  for (int i = 0; i < uRippleCount; i++) {
    vec3 r = uRipples[i];
    float dist = distance(p, r.xy);
    float ring = dist - r.z * 14.0;
    h += ${fl(CLICK_AMP)} * (1.0 - r.z / ${fl(CLICK_LIFE)}) * exp(-(ring * ring) / 4.8);
  }
  for (int i = 0; i < uWakeCount; i++) {
    vec4 w = uWake[i];
    float ring = (distance(p, w.xy) / w.y - ${fl(WAKE_RADIUS)} * w.z) / ${fl(WAKE_WIDTH)};
    if (abs(ring) > 3.0) continue;
    // Eased in so a new ripple swells up under the cursor rather than popping.
    float fade = smoothstep(0.0, 0.12, w.z) * (1.0 - w.z) * (1.0 - w.z);
    h += ${fl(WAKE_AMP)} * w.y * w.w * fade * exp(-0.5 * ring * ring);
  }
  return h;
}

float height(vec2 p) {
  float h = field(p).g;
  // Second octave, rotated and scaled, to break up tile repetition.
  h += 0.45 * field(vec2(p.x * 0.36 - p.y * 0.14 + 11.0, p.x * 0.14 + p.y * 0.36 + 5.0)).g;
  return h * uGain + disturbance(p);
}

void main() {
  float focal = 0.75 * uViewH;
  float horizonY = ${fl(HORIZON)} * uViewH;

  float invZ = mix(INV_FAR, INV_NEAR, aUV.y);
  float z = 1.0 / invZ;
  float rowW = z * uView.x / focal * ${fl(OVERSCAN)} + ${fl(2 * SIDE_MARGIN)};
  vec2 p = vec2((aUV.x - 0.5) * rowW, z);

  vec3 d = field(p);
  float h = height(p);
  float wx = p.x + CHOP * uGain * d.r;
  float wz = p.y + CHOP * uGain * d.b;
  if (wz < 1.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }

  // Surface normal from the full height function, so ripples and the wake are lit too.
  float e = 0.6;
  vec3 n = normalize(vec3(h - height(p + vec2(e, 0.0)), e, h - height(p + vec2(0.0, e))));

  // Jacobian of the choppy displacement: below 1 where the surface pinches
  // into a crest, which is where the original puts its foam.
  vec3 r = field(p + vec2(TEXEL, 0.0));
  vec3 l = field(p - vec2(TEXEL, 0.0));
  vec3 t = field(p + vec2(0.0, TEXEL));
  vec3 b = field(p - vec2(0.0, TEXEL));
  float c = CHOP * uGain / (2.0 * TEXEL);
  float jac = (1.0 + c * (r.r - l.r)) * (1.0 + c * (t.b - b.b)) - c * (t.r - b.r) * c * (r.b - l.b);
  float foam = 1.0 - smoothstep(0.35, 0.95, jac);

  float crest = clamp(h / ${fl(TARGET_AMP * 2)} + 0.5, 0.0, 1.0);
  // Faces tilted towards the viewer sit in their own shade; backs catch the light.
  float shade = clamp((LIGHT.y - dot(n, LIGHT)) * 2.2, -1.0, 1.0);
  float tone = clamp(crest * 0.7 + shade * 0.45 + foam * 0.35, 0.0, 1.0);
  vec3 color = tone < 0.55 ? mix(PALE, MID, tone / 0.55) : mix(MID, INK, (tone - 0.55) / 0.45);

  float nearness = clamp((invZ - INV_FAR) / (INV_VIS_NEAR - INV_FAR), 0.0, 1.0);
  float alpha = (0.10 + 0.50 * pow(nearness, 0.85)) * (0.45 + 1.0 * tone);
  vColor = vec4(color, clamp(alpha, 0.0, 1.0));

  float sizeBase = clamp(uViewH / 800.0, 0.8, 1.4);
  gl_PointSize = (1.0 + 1.9 * pow(nearness, 1.5)) * sizeBase * (1.0 + 0.5 * tone * tone) * uDpr;

  float sx = uView.x * 0.5 + wx * focal / wz;
  float sy = horizonY + (CAM_HEIGHT - h) * focal / wz;
  gl_Position = vec4(sx / uView.x * 2.0 - 1.0, 1.0 - sy / uView.y * 2.0, 0.0, 1.0);
}`;

const PARTICLE_FS = `#version 300 es
precision mediump float;
in vec4 vColor;
out vec4 outColor;
void main() {
  float a = smoothstep(0.5, 0.3, length(gl_PointCoord - 0.5)); // round, soft-edged
  if (a <= 0.0) discard;
  outColor = vec4(vColor.rgb, vColor.a * a);
}`;

// The sky gradient starts and ends on #d9e8f5 — the same color html.ocean-page
// paints — because iOS Safari tints the status-bar and toolbar zones with the
// page background color and nothing else (theme-color is ignored, and page
// pixels can't render there). Matching edge colors makes the canvas blend
// seamlessly into those zones, with a bright band at the horizon.
const SKY_CSS = ['#d9e8f5', '#fdfeff', '#f6fafd'];
const SKY_VS = `#version 300 es
void main() {
  vec2 p = vec2(gl_VertexID == 1 ? 3.0 : -1.0, gl_VertexID == 2 ? 3.0 : -1.0);
  gl_Position = vec4(p, 0.0, 1.0);
}`;
const SKY_FS = `#version 300 es
precision highp float;
uniform vec2 uView;
uniform float uViewH;
uniform float uDpr;
out vec4 outColor;
const vec3 EDGE = vec3(217.0, 232.0, 245.0) / 255.0;
const vec3 GLOW = vec3(253.0, 254.0, 255.0) / 255.0;
const vec3 HAZE = vec3(246.0, 250.0, 253.0) / 255.0;
void main() {
  // Stops anchored to the screen, not the canvas (which overhangs it on iOS).
  float y = uView.y - gl_FragCoord.y / uDpr;
  float glowY = ${fl(HORIZON - 0.04)} * uViewH;
  float hazeY = ${fl(HORIZON)} * uViewH;
  vec3 c = y < glowY
    ? mix(EDGE, GLOW, y / glowY)
    : y < hazeY
      ? mix(GLOW, HAZE, (y - glowY) / (hazeY - glowY))
      : mix(HAZE, EDGE, (y - hazeY) / max(1.0, uView.y - hazeY));
  outColor = vec4(c, 1.0);
}`;

function compile(gl, vsSource, fsSource) {
  const program = gl.createProgram();
  for (const [type, source] of [[gl.VERTEX_SHADER, vsSource], [gl.FRAGMENT_SHADER, fsSource]]) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(shader));
    }
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program));
  }
  const uniforms = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i++) {
    const name = gl.getActiveUniform(program, i).name.replace(/\[0\]$/, '');
    uniforms[name] = gl.getUniformLocation(program, name);
  }
  return { program, uniforms };
}

/** GPU resources for one context. Rebuilt from scratch if the context is lost. */
function createRenderer(gl) {
  const particles = compile(gl, PARTICLE_VS, PARTICLE_FS);
  const sky = compile(gl, SKY_VS, SKY_FS);

  // RGBA16F rather than 32F: half-float textures filter linearly on every
  // WebGL2 device, and float data uploads into them directly.
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, N, N, 0, gl.RGBA, gl.FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

  const lattice = gl.createVertexArray();
  const noAttributes = gl.createVertexArray();
  const buffer = gl.createBuffer();
  gl.bindVertexArray(lattice);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

  gl.disable(gl.DEPTH_TEST);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  let rows = 0;
  let cols = 0;

  return {
    // Sizes the lattice so particles sit about SPACING px apart on screen.
    layout(width, viewH) {
      let nextCols = Math.max(160, Math.round((width * OVERSCAN) / SPACING));
      let nextRows = Math.max(160, Math.round((0.75 * viewH * CAM_HEIGHT) / NEAR_Z / SPACING));
      const over = Math.sqrt((nextCols * nextRows) / MAX_PARTICLES);
      if (over > 1) {
        nextCols = Math.floor(nextCols / over);
        nextRows = Math.floor(nextRows / over);
      }
      if (nextCols === cols && nextRows === rows) return;
      cols = nextCols;
      rows = nextRows;
      // Far rows first so near particles paint on top. Each point is jittered
      // within its cell so the lattice never reads as lines.
      const data = new Float32Array(rows * cols * 2);
      let o = 0;
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          data[o++] = (i + Math.random()) / cols;
          data[o++] = (j + Math.random()) / rows;
        }
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    },

    draw({ width, height, viewH, dpr, ocean, gain, ripples, rippleCount, wake, wakeCount }) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);

      gl.disable(gl.BLEND);
      gl.useProgram(sky.program);
      gl.uniform2f(sky.uniforms.uView, width, height);
      gl.uniform1f(sky.uniforms.uViewH, viewH);
      gl.uniform1f(sky.uniforms.uDpr, dpr);
      gl.bindVertexArray(noAttributes);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, N, N, gl.RGBA, gl.FLOAT, ocean.packed);

      gl.enable(gl.BLEND);
      gl.useProgram(particles.program);
      const u = particles.uniforms;
      gl.uniform1i(u.uDisp, 0);
      gl.uniform2f(u.uView, width, height);
      gl.uniform1f(u.uViewH, viewH);
      gl.uniform1f(u.uDpr, dpr);
      gl.uniform1f(u.uGain, gain);
      gl.uniform1i(u.uRippleCount, rippleCount);
      gl.uniform3fv(u.uRipples, ripples);
      gl.uniform1i(u.uWakeCount, wakeCount);
      gl.uniform4fv(u.uWake, wake);
      gl.bindVertexArray(lattice);
      gl.drawArrays(gl.POINTS, 0, rows * cols);
    },
  };
}

const ParticlesBackground = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Matching overscroll/behind-toolbar color while the ocean is mounted
    // (same pattern as html.dark-page for Civitas).
    document.documentElement.classList.add('ocean-page');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, powerPreference: 'high-performance' });
    let renderer = null;
    const buildRenderer = () => {
      try {
        renderer = gl ? createRenderer(gl) : null;
      } catch (err) {
        console.error('ParticlesBackground:', err);
        renderer = null;
      }
    };
    buildRenderer();

    const ocean = createOcean();
    const ripples = []; // clicks
    const wake = []; // ripples a moving mouse cursor leaves behind it
    const rippleData = new Float32Array(MAX_RIPPLES * 3);
    const wakeData = new Float32Array(WAKE_MAX * 4);

    // Measures 100lvh — the physical screen height under viewport-fit=cover —
    // so the scene stays composed against the visible screen (horizon at
    // HORIZON of the screen, not of the canvas) while the canvas itself
    // overhangs the screen bottom by 50% on iOS and just gains extra water.
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:100vh;height:100lvh;visibility:hidden;pointer-events:none;';
    container.appendChild(probe);

    let width = 0;
    let height = 0;
    let viewH = 0;
    let dpr = 1;
    let rafId = 0;

    const sceneTime = () => performance.now() * 0.001 * TIME_SCALE;

    const drawFrame = (t) => {
      if (!renderer) return;
      ocean.update(t);

      for (let r = ripples.length - 1; r >= 0; r--) {
        if (t - ripples[r].t0 > CLICK_LIFE) ripples.splice(r, 1);
      }
      for (let r = wake.length - 1; r >= 0; r--) {
        if (t - wake[r].t0 > WAKE_LIFE) wake.splice(r, 1);
      }
      for (let r = 0; r < ripples.length; r++) {
        rippleData[r * 3] = ripples[r].x;
        rippleData[r * 3 + 1] = ripples[r].z;
        rippleData[r * 3 + 2] = t - ripples[r].t0;
      }
      for (let r = 0; r < wake.length; r++) {
        wakeData[r * 4] = wake[r].x;
        wakeData[r * 4 + 1] = wake[r].z;
        wakeData[r * 4 + 2] = (t - wake[r].t0) / WAKE_LIFE;
        wakeData[r * 4 + 3] = wake[r].strength;
      }

      renderer.draw({
        width,
        height,
        viewH,
        dpr,
        ocean,
        gain: TARGET_AMP / (ocean.peak * 1.2),
        ripples: rippleData,
        rippleCount: ripples.length,
        wake: wakeData,
        wakeCount: wake.length,
      });
    };

    const resize = () => {
      container.style.transform = `translate3d(0, ${window.scrollY}px, 0)`;
      dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      width = container.clientWidth;
      height = container.clientHeight;
      viewH = Math.min(probe.offsetHeight || height, height);
      // Until the first frame (and for good where WebGL2 is unavailable) the
      // container shows the same sky the shader paints.
      container.style.background = `linear-gradient(${SKY_CSS[0]} 0, ${SKY_CSS[1]} ${(HORIZON - 0.04) * viewH}px, ${SKY_CSS[2]} ${HORIZON * viewH}px, ${SKY_CSS[0]} 100%)`;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      if (renderer) renderer.layout(width, viewH);
      // Resizing clears the canvas; with no animation loop, repaint it now.
      if (reducedMotion) drawFrame(0);
    };
    window.addEventListener('resize', resize);

    // iOS Safari composites real page pixels behind its Liquid Glass toolbar
    // only while the document holds a non-zero scroll offset; at scrollY 0 it
    // falls back to tinting the chrome with the body background color. The
    // 130px runway in index.css makes the document scrollable, and since
    // everything on the page is fixed, holding a 1px offset is invisible.
    // Staying near the top keeps runway room below, so the scroll-down
    // gesture can still minimize the toolbar.
    const holdRunwayScroll = () => {
      if (window.scrollY < 1 && document.documentElement.scrollHeight > window.innerHeight) {
        window.scrollTo(0, 1);
      }
    };

    // The canvas lives in the document (not fixed) for the toolbar's sake, so
    // any document scroll would drag it with the page. The one scroll we can't
    // prevent is Safari's own keyboard avoidance: focusing the game's input
    // scrolls the runway to bring the field above the keyboard. Counter-
    // translating by the scroll offset keeps the ocean pinned to the screen
    // exactly like the fixed card above it, whatever the offset is.
    const pinToViewport = () => {
      container.style.transform = `translate3d(0, ${window.scrollY}px, 0)`;
    };

    const onScroll = () => {
      holdRunwayScroll();
      pinToViewport();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('scroll', pinToViewport);

    // The runway exists only for Safari's benefit; the visitor must never be
    // able to drag the page itself. Swallow touch panning unless the finger is
    // on something that genuinely overflows (the home card on a short phone),
    // in which case that element scrolls and overscroll-behavior stops the
    // gesture chaining up to the document.
    const blockPageTouchScroll = (e) => {
      let node = e.target;
      while (node && node !== document.body) {
        if (node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1) {
          const { overflowY, overflowX } = window.getComputedStyle(node);
          if (/(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflowX)) return;
        }
        node = node.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', blockPageTouchScroll, { passive: false });

    // Screen point (canvas-relative) -> point on the water plane, or null above the horizon.
    const toWater = (sx, sy) => {
      const focal = 0.75 * viewH;
      const horizonY = HORIZON * viewH;
      if (sy <= horizonY + 4) return null;
      const z = Math.min(FAR_Z, (CAM_HEIGHT * focal) / (sy - horizonY));
      return { x: ((sx - width / 2) * z) / focal, z };
    };

    const onPointerDown = (e) => {
      // Primary button or a touch only: not right or middle click, and not
      // ctrl-click, which opens the context menu on a Mac.
      if (e.button !== 0 || e.ctrlKey) return;
      const rect = canvas.getBoundingClientRect();
      const p = toWater(e.clientX - rect.left, e.clientY - rect.top);
      if (!p) return;
      ripples.push({ ...p, t0: sceneTime() });
      if (ripples.length > MAX_RIPPLES) ripples.shift();
    };
    canvas.addEventListener('pointerdown', onPointerDown);

    // Desktop only: a mouse moving over the water leaves a trail of small
    // ripples, one every WAKE_STEP px of travel, taller the faster it moves.
    let lastDrop = null; // where and when the last ripple (or the start of this stroke) was
    let lastMove = 0;
    const onPointerMove = (e) => {
      if (e.pointerType !== 'mouse') return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const now = performance.now();
      // Cursor came back after being away (off the water, over the card):
      // measure travel from here, not from where it left.
      if (!lastDrop || now - lastMove > 250) lastDrop = { x, y, at: now };
      lastMove = now;
      const dist = Math.hypot(x - lastDrop.x, y - lastDrop.y);
      const dt = now - lastDrop.at;
      if (dist < WAKE_STEP || dt < WAKE_INTERVAL) return;
      lastDrop = { x, y, at: now };
      const p = toWater(x, y);
      if (!p) return;
      const strength = 0.35 + 0.65 * Math.min(1, dist / dt / WAKE_FULL_SPEED);
      wake.push({ ...p, t0: sceneTime(), strength });
      if (wake.length > WAKE_MAX) wake.shift();
    };
    const hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (WAKE_ENABLED && hasMouse && !reducedMotion) {
      canvas.addEventListener('pointermove', onPointerMove);
    }

    const loop = () => {
      drawFrame(sceneTime());
      rafId = requestAnimationFrame(loop);
    };
    const start = () => {
      resize();
      if (reducedMotion) drawFrame(0);
      else rafId = requestAnimationFrame(loop);
    };
    start();

    // The browser may drop the GL context (GPU reset, too many contexts, a
    // long time in the background). Everything on the GPU is rebuilt when it
    // comes back.
    const onContextLost = (e) => {
      e.preventDefault();
      cancelAnimationFrame(rafId);
      renderer = null;
    };
    const onContextRestored = () => {
      buildRenderer();
      start();
    };
    canvas.addEventListener('webglcontextlost', onContextLost);
    canvas.addEventListener('webglcontextrestored', onContextRestored);

    return () => {
      document.documentElement.classList.remove('ocean-page');
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      if (vv) vv.removeEventListener('scroll', pinToViewport);
      container.style.transform = '';
      document.removeEventListener('touchmove', blockPageTouchScroll);
      probe.remove();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      canvas.removeEventListener('webglcontextrestored', onContextRestored);
    };
  }, []);

  return (
    <div id="particles-js" ref={containerRef} className="particles-ocean">
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
};

export default ParticlesBackground;
